import React, { useMemo, useReducer, useContext, useEffect } from "react";
import { useCookies } from "react-cookie";
import { notification } from "antd";
import { useNavigate } from "react-router-dom";

import { cleanupLocalStorageCache } from "hooks/useLocalStorageCache";

import {
  api,
  createAxiosAuthorization,
  handleErrors,
  removeAxiosAuthorization,
} from "helpers/api";

import reducer, {
  initialState,
  SET_LOGGED,
  SET_LOADING,
  SET_USER,
  SET_OTP_AUTH_NEEDED,
  SET_USER_EXPIRATION_DATE,
  SET_USER_ISSUES_SETTLEMENT_COLUMNS,
  UPDATE_COMPANY,
  SET_USER_ISSUES_INDEX_COLUMNS,
  SET_USER_CLIENTS_INDEX_COLUMNS,
} from "../reducers/auth";
import { useSettings } from "./settings";

const AuthContext = React.createContext();

function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);

  const { handleSetMode } = useSettings();

  const [{ access_token, refresh_token }, setCookie, removeCookie] = useCookies(
    ["access_token", "refresh_token"]
  );
  useEffect(() => {
    if (Boolean(access_token)) {
      checkAuthorization(access_token, refresh_token);
    } else {
      setLogged(false);
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleSessionRefresh = ({ detail }) => {
      checkAuthorization(detail.access_token, detail.refresh_token);
    };

    document.addEventListener("session-refreshed", handleSessionRefresh);
    return () => document.removeEventListener("session-refreshed", handleSessionRefresh);
    // eslint-disable-next-line
  }, []);

  const setLoading = (is_loading) =>
    dispatch({
      type: SET_LOADING,
      payload: { is_loading },
    });

  const setLogged = (is_logged) =>
    dispatch({ type: SET_LOGGED, payload: { is_logged } });

  const setUser = (user) => dispatch({ type: SET_USER, payload: { user } });

  const checkAuthorization = async (access_token, refresh_token) => {
    try {
      const { data } = await api.get("/users/me", {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });
      createAxiosAuthorization(access_token, refresh_token);
      setUser(data);
      setLogged(true);
      setLoading(false);
      handleSetMode(data?.theme || "light", false);
      cleanupLocalStorageCache();
    } catch (error) {
      if (error?.response?.data?.error_type === "otp_verification_required") {
        dispatch({ type: SET_OTP_AUTH_NEEDED });
      } else {
        removeCookie("access_token");
        removeCookie("refresh_token");
        notification.error({
          message: handleErrors(error),
        });
        setLogged(false);
        setLoading(false);
      }
    }
  };

  const handleLogin = async ({ email, password }) => {
    try {
      const { access_token, refresh_token } = await api.post("/users/sign_in", {
        email,
        password,
      });

      setCookie("access_token", access_token, {
        path: "/",
        maxAge: 8 * 3600,
      });
      setCookie("refresh_token", refresh_token, {
        path: "/",
        maxAge: 8 * 3600,
      });
      setCookie("is_view_blocked", false);
      navigate("/");
      checkAuthorization(access_token, refresh_token);
    } catch (error) {
      throw error;
    }
  };

  const handleRegister = async ({
    company_name,
    company_nip,
    first_name,
    last_name,
    email,
    phone,
    password,
    password_confirmation,
  }) => {
    try {
      await api.post("/users", {
        company_name,
        company_nip,
        first_name,
        last_name,
        email,
        phone,
        password,
        password_confirmation,
      });
      notification.success({
        message:
          "Zarejestrowano pomyślnie. Na podany adres e-mail został wysłany link aktywacyjny.",
      });
      navigate("/");
    } catch (error) {
      throw error;
    }
  };

  const handleConfirmInvitation = async ({
    worker_invitation_id,
    password,
    password_confirmation,
  }) => {
    try {
      await api.post(`/worker_invitations/${worker_invitation_id}/confirm`, {
        password,
        password_confirmation,
      });
      notification.success({
        message:
          "Zarejestrowano pomyślnie. Na podany adres e-mail został wysłany link aktywacyjny.",
      });
      navigate("/");
    } catch (error) {
      throw error;
    }
  };

  const handleConfirmEmail = async (confirmation_token) => {
    try {
      const { message } = await api.post("/users/confirm_email", {
        confirmation_token,
      });
      notification.success({
        message,
      });
    } catch (error) {
      notification.error({
        message: handleErrors(error),
      });
    } finally {
      navigate("/");
    }
  };

  const handleResetPassword = async ({ email }) => {
    try {
      const { message } = await api.post("/passwords", {
        email,
      });
      return message;
    } catch (error) {
      throw error;
    }
  };

  const handleChangePassword = async ({
    password,
    password_confirmation,
    reset_password_token,
  }) => {
    try {
      const { message } = await api.put("/passwords/reset_password", {
        password,
        password_confirmation,
        reset_password_token,
      });
      localStorage.removeItem("account_blocked_to");
      return message;
    } catch (error) {
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/users/sign_out");

      removeAxiosAuthorization();
      removeCookie("access_token");
      removeCookie("refresh_token");
      setLogged(false);
      setUser(null);
      handleSetMode("light", false);
      notification.success({
        message: "Wylogowano pomyślnie",
      });
      navigate("/");
    } catch (error) {
      throw error;
    }
  };

  const verifyOtp = async (code) => {
    try {
      await api.post(
        "/users/verify_otp",
        {
          code,
        },
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        }
      );
      checkAuthorization(access_token, refresh_token);
    } catch (error) {
      throw error;
    }
  };

  const handleLogoutLocally = () => {
    removeAxiosAuthorization();
    removeCookie("access_token");
    removeCookie("refresh_token");
    setLogged(false);
    setUser(null);
    navigate("/");
    notification.error({
      message: "Twoja sesja wygasła",
    });
  };

  const cancelOtp = async () => {
    try {
      await api.post(
        "/users/cancel_otp",
        {},
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        }
      );
      removeAxiosAuthorization();
      removeCookie("access_token");
      removeCookie("refresh_token");
      setLogged(false);
      setUser(null);
      navigate("/");
    } catch (error) {
      throw error;
    }
  };

  const verifyPin = async (pin) => {
    try {
      await api.get(`/users/verify_pin?pin=${pin}`);
    } catch (error) {
      throw error;
    }
  };

  const updatePin = async ({ old_pin, pin }) => {
    try {
      await api.put("/users/change_pin", { old_pin, pin });
      notification.success({
        message: "Pin zaktualizowany",
      });
    } catch (error) {
      notification.error({
        message: handleErrors(error),
      });
      throw error;
    }
  };

  const handleUpdateSessionExpirationDate = ({ expiration_date }) =>
    dispatch({
      type: SET_USER_EXPIRATION_DATE,
      payload: { expiration_date },
    });

  const updateUserIssueSettlementColumns = async (
    issues_settlement_columns
  ) => {
    try {
      const { data } = await api.put("/users/issues_settlement_columns", {
        issues_settlement_columns,
      });

      dispatch({
        type: SET_USER_ISSUES_SETTLEMENT_COLUMNS,
        payload: {
          issues_settlement_columns: data.issues_settlement_columns,
        },
      });
    } catch (error) {
      throw error;
    }
  };

  const updateUserIssueIndexColumns = async (issues_index_columns) => {
    try {
      const { data } = await api.put("/users/issues_index_columns", {
        issues_index_columns,
      });

      dispatch({
        type: SET_USER_ISSUES_INDEX_COLUMNS,
        payload: {
          issues_index_columns: data.issues_index_columns,
        },
      });
    } catch (error) {
      throw error;
    }
  };

  const updateUserClientsIndexColumns = async (clients_index_columns) => {
    try {
      const { data } = await api.put("/users/clients_index_columns", {
        clients_index_columns,
      });

      dispatch({
        type: SET_USER_CLIENTS_INDEX_COLUMNS,
        payload: {
          clients_index_columns: data.clients_index_columns,
        },
      });
    } catch (error) {
      throw error;
    }
  };

  const updateCompany = (company_data) =>
    dispatch({ type: UPDATE_COMPANY, payload: { company_data } });

  const deleteCompany = async ({ password }) => {
    try {
      await api.post("/users/delete_company", {
        password,
      });
      removeAxiosAuthorization();
      removeCookie("access_token");
      removeCookie("refresh_token");
      setLogged(false);
      setUser(null);
      notification.success({
        message: "Firma oraz powiązane z nią dane zostały usunięte.",
      });

      navigate("/");
    } catch (error) {
      notification.error({
        message: handleErrors(error),
      });
      throw error;
    }
  };

  const changePassword = async ({
    current_password,
    password,
    password_confirmation,
  }) => {
    try {
      await api.put("/passwords/change", {
        current_password,
        password,
        password_confirmation,
      });
      notification.success({
        message: "Hasło zostało zaktualizowane",
      });
    } catch (error) {
      notification.error({
        message: handleErrors(error),
      });
      throw error;
    }
  };

  const value = useMemo(() => {
    return {
      state,
      handleRegister,
      handleConfirmEmail,
      handleLogin,
      handleLogout,
      handleLogoutLocally,
      verifyOtp,
      cancelOtp,
      updatePin,
      verifyPin,
      handleUpdateSessionExpirationDate,
      updateUserIssueSettlementColumns,
      updateUserClientsIndexColumns,
      updateUserIssueIndexColumns,
      handleResetPassword,
      handleChangePassword,
      updateCompany,
      handleConfirmInvitation,
      deleteCompany,
      changePassword,
    };
    // eslint-disable-next-line
  }, [state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

const useAuth = () => useContext(AuthContext);
export { useAuth };
export default AuthProvider;
